import { useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Inbox } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { TxRow } from '../lib/api';
import { formatDateTime, formatVnd, formatAmountOriginal } from '../lib/format';
import { EmptyState } from './ui/empty-state';
import { Badge } from './ui/badge';
import { DataTable } from './shared/data-table';
import { DataTableColumnHeader } from './shared/data-table-column-header';

const TYPE_VARIANT: Record<TxRow['type'], 'default' | 'info' | 'warning' | 'destructive'> = {
  purchase: 'default',
  refund: 'info',
  fee: 'warning',
  decline: 'destructive',
};

const TX_COLUMNS: ColumnDef<TxRow, unknown>[] = [
  {
    id: 'occurred_at',
    accessorKey: 'occurred_at',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }) => (
      <span className="tabular-nums text-muted-foreground">
        {formatDateTime(row.original.occurred_at)}
      </span>
    ),
  },
  {
    id: 'card',
    accessorKey: 'card_alias',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Card" />,
    cell: ({ row }) => <span className="text-muted-foreground">{row.original.card_alias}</span>,
  },
  {
    id: 'merchant',
    accessorKey: 'merchant_canonical',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Merchant" />,
    cell: ({ row }) => (
      <div>
        <div className="text-foreground">
          {row.original.merchant_canonical ?? row.original.raw_merchant_text}
        </div>
        {row.original.merchant_canonical &&
          row.original.merchant_canonical !== row.original.raw_merchant_text && (
            <div className="text-xs text-muted-foreground/70">{row.original.raw_merchant_text}</div>
          )}
      </div>
    ),
  },
  {
    id: 'amount_original',
    accessorKey: 'amount_original',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" align="right" />,
    cell: ({ row }) => (
      <span className="block text-right tabular-nums text-foreground">
        {formatAmountOriginal(row.original.amount_original, row.original.currency)}
      </span>
    ),
  },
  {
    id: 'amount_vnd',
    accessorKey: 'amount_vnd',
    header: ({ column }) => <DataTableColumnHeader column={column} title="VND" align="right" />,
    cell: ({ row }) => (
      <span className="block text-right tabular-nums text-muted-foreground">
        {formatVnd(row.original.amount_vnd)}
      </span>
    ),
  },
  {
    id: 'type',
    accessorKey: 'type',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
    cell: ({ row }) => <Badge variant={TYPE_VARIANT[row.original.type]}>{row.original.type}</Badge>,
  },
  {
    id: 'department',
    accessorKey: 'department_code',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Dept" />,
    cell: ({ row }) =>
      row.original.department_code ? (
        <span className="rounded bg-background px-2 py-0.5 font-mono text-xs text-muted-foreground">
          {row.original.department_code}
        </span>
      ) : (
        <span className="text-muted-foreground/70">—</span>
      ),
  },
  {
    id: 'actions',
    enableSorting: false,
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        {row.original.has_alert > 0 && <AlertTriangle size={12} className="text-warning" />}
        <ChevronRight size={14} strokeWidth={1.5} />
      </span>
    ),
  },
];

const TX_EMPTY = (
  <EmptyState
    icon={Inbox}
    title="Chưa có giao dịch nào khớp bộ lọc"
    description="Thử điều chỉnh bộ lọc hoặc chờ GAS đẩy email mới."
  />
);

export function TransactionTable({ rows }: { rows: TxRow[] }) {
  const navigate = useNavigate();

  return (
    <>
      {/* Desktop table — hidden on mobile */}
      <div className="hidden md:block">
        <DataTable
          columns={TX_COLUMNS}
          data={rows}
          emptyState={TX_EMPTY}
          onRowClick={(row) => navigate(`/transactions/${row.id}`)}
        />
      </div>

      {/* Mobile cards — hidden on desktop */}
      <div className="space-y-2 md:hidden">
        {rows.length === 0 && (
          <EmptyState
            icon={Inbox}
            title="Chưa có giao dịch nào khớp bộ lọc"
            description="Thử điều chỉnh bộ lọc hoặc chờ GAS đẩy email mới."
          />
        )}
        {rows.map((r) => (
          <Link
            key={r.id}
            to={`/transactions/${r.id}`}
            className="block rounded-lg border border-border bg-card p-3 transition-colors hover:border-input"
          >
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>{formatDateTime(r.occurred_at)}</span>
              <span className="flex items-center gap-1">
                {r.has_alert > 0 && <AlertTriangle size={12} className="text-warning" />}
                <span className="font-mono text-[10px]">
                  {r.bank_code}·{r.card_alias}
                </span>
              </span>
            </div>
            <div className="mb-2 truncate text-sm font-medium text-foreground">
              {r.merchant_canonical ?? r.raw_merchant_text}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-mono tabular-nums text-foreground">
                {formatVnd(r.amount_vnd)}
              </span>
              <div className="flex items-center gap-2 text-xs">
                <Badge variant={TYPE_VARIANT[r.type]}>{r.type}</Badge>
                {r.department_code ? (
                  <span className="rounded bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                    {r.department_code}
                  </span>
                ) : null}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}
